import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { track, identify } from "@/lib/posthogClient";
import { identify as swsIdentify, trackConversion } from "@/lib/analyticsClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EVERFIT_URL = "https://everfit.io/p/Q9TTSYVZYA";

export default function IntakeThanks() {
  const [params] = useSearchParams();
  const [posted, setPosted] = useState(false);

  const name = params.get("name") || "";
  const email = params.get("email") || "";

  useEffect(() => {
    if (posted) return;
    fetch(`${BACKEND}/api/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email || "intake-no-email-passed@unknown.local",
        name: name || "",
        source: "Coaching Intake (Jotform)",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        page: "/intake-thanks",
      }),
    }).catch(() => { /* silent */ });

    try {
      if (email) identify(email, { email, name });
      track("intake_submitted", { name, email, source: "Jotform" });
    } catch (err) { console.warn("IntakeThanks: analytics failed:", err); }

    // Sweat With Slay analytics
    try {
      if (email) swsIdentify(email, "lead", name || "");
      trackConversion("lead_form_submit", { email, refId: "intake_jotform" });
    } catch { /* no-op */ }

    setPosted(true);
  }, [email, name, posted]);

  return (
    <main data-testid="intake-thanks-page" className="bg-[#0A0A0A] text-white font-body min-h-screen flex items-center justify-center px-6 py-16">
      <div className="max-w-2xl w-full text-center">
        <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-[#FF1B91]/15 border border-[#FF1B91] mb-8">
          <CheckCircle size={36} weight="fill" className="text-[#FF1B91]" />
        </div>
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#FF1B91]" />
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Intake received</p>
          <span className="h-px w-10 bg-[#FF1B91]" />
        </div>
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter mb-6">
          {name ? <>Thanks, <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">{name}</span></> : <>You&apos;re <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">in</span></>}
        </h1>
        <p className="text-white/80 leading-relaxed mb-3 text-lg">
          I review every intake personally — usually within <strong className="text-white">48 hours</strong>.
        </p>
        <p className="text-white/65 leading-relaxed mb-10">
          Check your inbox{email ? ` (${email})` : ""} for my reply. While you wait, you can already join the Everfit app:
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
          <a
            href={EVERFIT_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="thanks-everfit-cta"
            className="inline-flex items-center justify-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
          >
            Join Everfit <ArrowRight size={14} weight="bold" />
          </a>
          <Link
            to="/"
            data-testid="thanks-home-cta"
            className="inline-flex items-center justify-center gap-3 border border-white/20 hover:border-[#FF1B91] hover:text-[#FF1B91] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em]"
          >
            <ArrowLeft size={14} weight="bold" /> Back home
          </Link>
        </div>

        <p className="text-[10px] uppercase tracking-[0.3em] text-white/35">
          Saved you a seat. — Slay 🩷
        </p>
      </div>
    </main>
  );
}
